import React, { useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useFormik } from "formik"
import * as Yup from "yup"
import moment from 'moment'
import { useNavigate } from 'react-router-dom'

// components
import AppCard from '../../components/AppCard'
import AppForm from '../../components/form/form-expense/AppForm'
import ActionButtonForm from 'src/components/form/general-input-form/ActionButtonForm'
import AppModalConfirmation from '../../components/modal/AppModalConfirmation'
import AppModalFilePreview from 'src/components/modal/AppModalFilePreview'

// helpers
import Axios from '../../helpers/axios'
import { 
  singleProjectsOptionsSelect, 
  singleExpenseIntervalOptionSelect, 
  singleExpenseCategoryOptionsSelect,
  singleExpenseTagsOptionsSelect,
  singleExpenseCategoryAirplaneOptionSelect,
  singleExpenseCategoryTransportOptionSelect,
  singleExpenseCategoryFuelOptionSelect,
  singleExpenseCategoryTripTypeOptionSelect,
  singleExpenseCategoryFlightOptionSelect,
  singleExpenseCategoryTrainOptionSelect,
  singleExpenseCategoryAccomodationOptionSelect
} from '../../helpers/format-options'
import useToastMessage from 'src/helpers/toast-message'

const AddExpense = () => {
  const navigate = useNavigate()
  const dispatch = useDispatch()
  const { showSuccess, showError } = useToastMessage();

  const isLoading = useSelector((state) => state.isLoading)

  const [projectOptions, setProjectOptions] = useState([])
  const [projectValue, setProjectValue] = useState(null)

  const [expenseCategoryOptions, setExpenseCategoryOptions] = useState([])
  const [expenseCategoryValue, setExpenseCategoryValue] = useState(null)
  const [expenseAirplaneValue, setExpenseAirplaneValue] = useState(null)
  const [expenseTransportationValue, setExpenseTransportationValue] = useState(null)
  const [expenseFuelValue, setExpenseFuelValue] = useState(null)
  const [expenseFlightValue, setExpenseFlightValue] = useState(null)
  const [expenseTrainValue, setExpenseTrainValue] = useState(null)
  const [expenseAccomodationValue, setExpenseAccomodationValue] = useState(null)
  const [expenseAccomodationProvinceValue, setExpenseAccomodationProvinceValue] = useState(null)
  const [expenseAccomodationCitiesValue, setExpenseAccomodationCitiesValue] = useState(null)
  const [expenseTagsOptions, setExpenseTagsOptions] = useState([])

  const [provinceOptions, setProvinceOptions] = useState([]);
  const [cityOptions, setCityOptions] = useState([]);

  const [intervalValue, setIntervalValue] = useState({
    label: 'No repeat',
    value: 'no repeat'
  })

  const [defaultCountryIdIndonesia, setDefaultCountryIdIndonesia] = useState('104')

  const [showDeleteItem, setShowDeleteItem] = useState({ show: false, item: {}, index: 0 })
  const [showModalConfirm, setShowModalConfirm] = useState({ show: false })

  const [openFilePreview, setOpenFilePreview] = useState(false)

  const formik = useFormik({
    initialValues: {
      expense_name: '',
      expense_description: '',
      expense_value: '',
      expense_date: '',
      expense_repeat_start_date: '',
      expense_repeat_end_date: '',
      expense_repeat_forever: false,
      expense_repeat_interval: 'no repeat',
      expense_notes: '',
      project_id: '',
      is_related_to_project: false,
      file: [],
      category_id: '',
      category_value: null,
      expense_flight: {
        reservation_number: "",
        airplane_name: "",
        flight_details: "",
        flight_type: "", // One Way / Return
        reservation_date_1: "",
        reservation_date_2: "",
        booking_through: "",
        other_booking_input: "",
        passengers: [
          { first_name: "", last_name: "" }
        ]
      },
      expense_accomodation: {
        reservation_number: "",
        accomodation_name: "",
        accomodation_details: "",
        accomodation_address: "",
        accomodation_country_id: defaultCountryIdIndonesia, // Default to Indonesia
        accomodation_province_id: "",
        accomodation_city_id: "",
        is_booking_more_than_one_day: false,
        reservation_date_1: "",
        reservation_date_2: "",
        booking_through: "",
        other_booking_input: "",
        guests: [
          { first_name: "", last_name: "" }
        ]
      },
      expense_train: {
        reservation_number: "",
        train_name: "",
        train_details: "",
        train_type: "",
        reservation_date_1: "",
        reservation_date_2: "",
        booking_through: "",
        other_booking_input: "",
        passengers: [
          { first_name: "", last_name: "" }
        ]
      },
      expense_car_rental: {
        rental_company: "",
        description: "",
        car_type: "",
        booking_start: "",
        booking_end: "",
        is_booking_more_than_one_day: false
      },
      expense_pettycash_operational: {
        datetime: "",
        description: "",
        tags: [""]
      },
      expense_meals: {
        restaurant_name: "",
        datetime: "",
        description: ""
      },
      expense_fuel: {
        company_name: "",
        other_company_input: "",
        datetime: "",
        description: ""
      },
      expense_etoll: {
        datetime: "",
        description: ""
      },
      expense_transportation: {
        datetime: "",
        transportation_category: "",
        other_transportation_input: "",
        description: ""
      },
      expense_office_supplies: {
        company: "",
        datetime: "",
        description: ""
      },
      expense_printing: {
        company: "",
        datetime: "",
        description: ""
      },
      expense_other_reimbursement: {
        reimbursement_for: "",
        company: "",
        datetime: "",
        description: ""
      }
    },
    validationSchema: Yup.object().shape({
      expense_name: Yup.string()
        .required("Please enter expense name."),
      expense_description: Yup.string()
        .required("Please enter expense description."),
      expense_value: Yup.string()
        .required("Please enter expense value."),
      expense_date: Yup.string()
        .when(['expense_repeat_interval'], {
          is: (expense_repeat_interval) => expense_repeat_interval === 'no repeat',
          then: () => Yup.string().required("Please select expense date."),
        }),
      expense_repeat_start_date: Yup.string()
        .when(['expense_repeat_interval'], {
          is: (expense_repeat_interval) => expense_repeat_interval !== 'no repeat',
          then: () => Yup.string().required("Please select expense repeat start date."),
        }),
      expense_repeat_end_date: Yup.string()
        .when(['expense_repeat_forever', 'expense_repeat_interval'], {
          is: (expense_repeat_forever, expense_repeat_interval) => expense_repeat_forever == false && expense_repeat_interval !== 'no repeat',
          then: () => Yup.string().required('Please select expense repeat end date.')
        }),
      expense_repeat_interval: Yup.string()
        .required("Please select expense repeat interval."),
      project_id: Yup.string()
        .when('is_related_to_project', {
          is: true,
          then: () => Yup.string().required('Please select project name')
        }),
      expense_flight: Yup.object().when("category_value", {
        is: (category_value) => category_value?.label == 'Flight',
        then: () => Yup.object({
          reservation_number: Yup.string().required('Please enter reservation number'),
          airplane_name: Yup.string().required('Please enter airplane name'),
          flight_details: Yup.string().required('Please enter flight detail'),
          flight_type: Yup.string().required('Please select flight type'),
          reservation_date_1: Yup.string()
          .when("flight_type", {
            is: (flight_type) => flight_type == 'One way' || flight_type == 'Return',
            then: (flight_type) => Yup.string().required(flight_type == 'One way' ? 'Please select flight date' : 'Please select flight departure date'),
          }),
          reservation_date_2: Yup.string()
          .when("flight_type", {
            is: (flight_type) => flight_type == 'Return',
            then: () => Yup.string().required('Please select flight return date')
          }),
          booking_through: Yup.string().required('Please select booking through'),
          other_booking_input: Yup.string().when("booking_through", {
            is: (booking_through) => booking_through == 'Others',
            then: () => Yup.string().required("Please specify")
          }),
          passengers: Yup.array().of(
            Yup.object({
              first_name: Yup.string().required('Please enter first name'),
              last_name: Yup.string().required('Please enter last name')
            })
          ).min(1, 'Please add at least one passenger')
        })
      }),
      expense_accomodation: Yup.object().when("category_value", {
        is: (category_value) => category_value?.label == 'Accomodation',
        then: () => Yup.object({
          reservation_number: Yup.string().required('Please enter reservation number'),
          accomodation_name: Yup.string().required('Please enter accomodation name'),
          accomodation_details: Yup.string().required('Please enter accomodation detail'),
          accomodation_address: Yup.string().required('Please enter accomodation address'),
          accomodation_province_id: Yup.string().required('Please select accomodation province'),
          accomodation_city_id: Yup.string().required('Please select accomodation city'),
          is_booking_more_than_one_day: Yup.boolean(),
          reservation_date_1: Yup.string()
          .when("is_booking_more_than_one_day", {
            is: (is_booking_more_than_one_day) => is_booking_more_than_one_day == true || is_booking_more_than_one_day == false,
            then: (is_booking_more_than_one_day) => Yup.string().required(is_booking_more_than_one_day ? 'Please select reservation start date' : 'Please select reservation date'),
          }),
          reservation_date_2: Yup.string()
          .when("is_booking_more_than_one_day", {
            is: (is_booking_more_than_one_day) => is_booking_more_than_one_day == true,
            then: () => Yup.string().required('Please select reservation end date')
          }),
          booking_through: Yup.string().required('Please select booking through'),
          other_booking_input: Yup.string().when("booking_through", {
            is: (booking_through) => booking_through == 'Others',
            then: () => Yup.string().required("Please specify")
          }),
          guests: Yup.array().of(
            Yup.object({
              first_name: Yup.string().required('Please enter first name'),
              last_name: Yup.string().required('Please enter last name')
            })
          ).min(1, 'Please add at least one guest')
        })
      }),
      expense_train: Yup.object().when("category_value", {
        is: (category_value) => category_value?.label == 'Train',
        then: () => Yup.object({
          reservation_number: Yup.string().required('Please enter reservation number'),
          train_name: Yup.string().required('Please enter train name'),
          train_details: Yup.string().required('Please enter train detail'),
          train_type: Yup.string().required('Please select train type'),
          reservation_date_1: Yup.string()
          .when("train_type", {
            is: (train_type) => train_type == 'One way' || train_type == 'Return' || train_type == '',
            then: (train_type) => Yup.string().required(train_type !== '' ? train_type == 'One way' ? 'Please select train date' : 'Please select train departure date' : 'Please select train date'),
          }),
          reservation_date_2: Yup.string()
          .when("train_type", {
            is: (train_type) => train_type == 'Return',
            then: () => Yup.string().required('Please select train return date')
          }),
          booking_through: Yup.string().required(),
          other_booking_input: Yup.string().when("booking_through", {
            is: (booking_through) => booking_through == 'Others',
            then: () => Yup.string().required("Please specify")
          }),
          passengers: Yup.array().of(
            Yup.object({
              first_name: Yup.string().required('Please enter first name'),
              last_name: Yup.string().required('Please enter last name')
            })
          ).min(1, 'Please add at least one passenger')
        })
      }),
      expense_car_rental: Yup.object().when("category_value", {
        is: (category_value) => category_value?.label == 'Car rental',
        then: () => Yup.object({
          rental_company: Yup.string().required('Please enter company'),
          description: Yup.string().required('Please enter description'),
          car_type: Yup.string().required('Please enter car type'),
          is_booking_more_than_one_day: Yup.boolean(),
          booking_start: Yup.string()
          .when("is_booking_more_than_one_day", {
            is: (is_booking_more_than_one_day) => is_booking_more_than_one_day == true || is_booking_more_than_one_day == false,
            then: (is_booking_more_than_one_day) => Yup.string().required(is_booking_more_than_one_day ? 'Please select booking start date' : 'Please select booking period'),
          }),
          booking_end: Yup.string()
          .when("is_booking_more_than_one_day", {
            is: (is_booking_more_than_one_day) => is_booking_more_than_one_day == true,
            then: () => Yup.string().required('Please select booking end date')
          }),
        })
      }),
      expense_pettycash_operational: Yup.object().when("category_value", {
        is: (category_value) => {
          category_value?.label == 'Pettycash & Operational'
        },
        then: () => Yup.object({
          datetime: Yup.string().required('Please select date time'),
          description: Yup.string().required('Please enter description'),
          tags: Yup.array().of(Yup.string().required())
        })
      }),
      expense_meals: Yup.object()
        .when("category_value", {
          is: (category_value) => category_value?.label === 'Meals',
          then: () => Yup.object({
            restaurant_name: Yup.string().required('Please enter restaurant name'),
            datetime: Yup.string().required('Please select date time'),
            description: Yup.string().required('Please enter description')
          })
        }),
      expense_fuel: Yup.object().when("category_value", {
       is: (category_value) => category_value?.label == 'Fuel',
        then: () => Yup.object({
          company_name: Yup.string().required('Please enter company name'),
          other_company_input: Yup.string().when("company_name", {
            is: (company_name) => company_name == "Others",
            then: () => Yup.string().required("Please specify")
          }),
          datetime: Yup.string().required('Please select date time'),
          description: Yup.string().required('Please enter description')
        })
      }),
      expense_etoll: Yup.object().when("category_value", {
        is: (category_value) => category_value?.label == 'E-toll money',
        then: () => Yup.object({
          datetime: Yup.string().required('Please select date time'),
          description: Yup.string().required('Please enter description')
        })
      }),
      expense_transportation: Yup.object().when("category_value", {
        is: (category_value) => category_value?.label == 'Transportation',
        then: () => Yup.object({
          datetime: Yup.string().required('Please select date time'),
          transportation_category: Yup.string().required('Please select transportation'),
          other_transportation_input: Yup.string().when("transportation_category", {
            is: (transportation_category) => transportation_category == "Others",
            then: () => Yup.string().required("Please specify")
          }),
          description: Yup.string().required('Please enter description')
        })
      }),
      expense_office_supplies: Yup.object().when("category_value", {
        is: (category_value) => category_value?.label == 'Office supplies / stationery',
        then: () => Yup.object({
          company: Yup.string().required('Please enter company'),
          datetime: Yup.string().required('Please select datetime'),
          description: Yup.string().required('Please enter description')
        })
      }),
      expense_printing: Yup.object().when("category_value", {
        is: (category_value) => category_value?.label == 'Printing',
        then: () => Yup.object({
          company: Yup.string().required('Please enter company'),
          datetime: Yup.string().required('Please select datetime'),
          description: Yup.string().required('Please enter description')
        })
      }),
      expense_other_reimbursement: Yup.object().when("category_value", {
        is: (category_value) => category_value?.label == 'Other reimbursement',
        then: () => Yup.object({
          reimbursement_for: Yup.string().required('Please enter reimbursement for'),
          company: Yup.string().required('Please enter company'),
          datetime: Yup.string().required('Please select date time'),
          description: Yup.string().required('Please enter description')
        })
      })
    }),
    validateOnBlur: false,
    validateOnChange: false,
    onSubmit: async (values, { setSubmitting }) => {
      handleSave(values)
      setSubmitting(false)
    },
  });

  useEffect(() => {
    fetchAllProject()
    fetchAllExpenseCategories()
    fetchAllExpenseTags()
  }, [])

  useEffect(() => {
    if (expenseCategoryValue?.label == 'Accomodation') {
      fetchAllProvince()
    }
  }, [expenseCategoryValue])

  const fetchAllProject = () => {
    Axios({
      method: "get",
      url: "/api/tracker/v1/project",
    })
      .then(({ data }) => {
        if (data?.success) {
          setProjectOptions(singleProjectsOptionsSelect(data.value))
        }
      })
      .catch((err) => {
        console.error(err);
        const errMsg = err?.response?.data?.message
        showError(errMsg)
      });
  }

  const fetchAllExpenseCategories = () => {
    Axios({
      method: "get",
      url: "/api/tracker/v1/expense/category/option/list",
    })
      .then(({ data }) => {
        if (data?.success) {
          setExpenseCategoryOptions(singleExpenseCategoryOptionsSelect(data.value))
        }
      })
      .catch((err) => {
        console.error(err);
        const errMsg = err?.response?.data?.message
        showError(errMsg)
      });
  }

  const fetchAllExpenseTags = () => {
    Axios({
      method: "get",
      url: "/api/tracker/v1/expense/tags/option/list",
    })
      .then(({ data }) => {
        if (data?.success) {
          setExpenseTagsOptions(singleExpenseTagsOptionsSelect(data.value))
        }
      })
      .catch((err) => {
        console.error(err);
        const errMsg = err?.response?.data?.message
        showError(errMsg)
      });
  };
  
  const fetchAllCountries = () => {
    Axios({
      method: "get",
      url: "/api/tracker/v1/countries",
    })
      .then(({ data }) => {
        if (data?.length > 0) {
          let countryIdIndo = data?.filter((country) => country?.name?.includes('Indonesia'))?.map((val) => val?.id)
          setDefaultCountryIdIndonesia(countryIdIndo)
        }
      })
      .catch((err) => {
        console.error(err);
        const errMsg = err?.response?.data?.message
        showError(errMsg)
      });
  };


  const fetchAllProvince = () => {
    Axios({
      method: "get",
      url: "/api/tracker/v1/countries/provinces",
    })
      .then(({ data }) => {
        if (data) {
          let finalProvinceOptions = data;
          finalProvinceOptions = finalProvinceOptions.map((prov) => {
            let objProv = {
              label: prov.province_name,
              value: prov.province_id,
            };
            return objProv;
          });
          setProvinceOptions(finalProvinceOptions);
        }
      })
      .catch((err) => {
        console.error(err);
        const errMsg = err?.response?.data?.message
        showError(errMsg)
      });
  }

  const fetchAllCities = (provinceId) => {
    Axios({
      method: "get",
      url: `/api/tracker/v1/countries/cities/${provinceId}`,
    })
      .then(({ data }) => {
        if (data) {
          let finalCityOptions = data;
          finalCityOptions = finalCityOptions.map((city) => {
            let objCity = {
              label: city.city_name,
              value: city.city_id,
            };
            return objCity;
          });
          setCityOptions(finalCityOptions);
        }
      })
      .catch((err) => {
        console.error(err);
        const errMsg = err?.response?.data?.message
        showError(errMsg)
      });
  }

  const handleOnDateChange = (date, name) => {
    formik.setValues((values) => ({
      ...values,
      [name]: moment(date).format('YYYY-MM-DD HH:mm:ss')
    }), false)
  }

  const handleInputValue = (values) => {
    const formData = new FormData()

    formData.append('name', values?.expense_name)
    formData.append('description', values?.expense_description)
    formData.append('value', parseFloat(values?.expense_value?.replace(/,/g, "")))
    if (values?.expense_repeat_interval == 'no repeat') {
      formData.append('date', values?.expense_date)
    } else {
      formData.append('repeat_start_date', values?.expense_repeat_start_date)
      formData.append('repeat_end_date', values?.expense_repeat_end_date)
    }
    formData.append('repeat_forever', values.expense_repeat_forever)
    formData.append('repeat_interval', values?.expense_repeat_interval)
    formData.append('notes', values?.expense_notes)
    if (values.file?.length > 0) {
      formData.append('file', values?.file[0])
    }
    if (values?.is_related_to_project) {
      formData.append('project_id', values?.project_id)
    }
    if (values?.category_id) {
      let type = values?.category_value?.label
      formData.append('category_type', values?.category_value?.label)
      formData.append('category_id', values?.category_id)

      switch (type) {
        case 'Flight':
          if (values?.expense_flight) {
            formData.append('flight', JSON.stringify(values?.expense_flight))
          }
          break;
        case 'Accomodation':
          if (values?.expense_accomodation) {
            formData.append('accomodation', JSON.stringify(values?.expense_accomodation))
          }
          break;
        case 'Train':
          if (values?.expense_train) {
            formData.append('train', JSON.stringify(values?.expense_train))
          }
          break;
        case 'Car rental':
          if (values?.expense_car_rental) {
            formData.append('car_rental', JSON.stringify(values?.expense_car_rental))
          }
          break;
        case 'Pettycash & Operational':
          if (values?.expense_pettycash_operational) {
            formData.append('pettycash_operational', JSON.stringify(values?.expense_pettycash_operational))
          }
          break;
        case 'Meals':
          if (values?.expense_meals) {
            formData.append('meal', JSON.stringify(values?.expense_meals))
          }
          break;
        case 'Fuel':
          if (values?.expense_fuel) {
            formData.append('fuel', JSON.stringify(values?.expense_fuel))
          }
          break;
        case 'E-toll money':
          if (values?.expense_etoll) {
            formData.append('etoll_money', JSON.stringify(values?.expense_etoll))
          }
          break;
        case 'Transportation':
          if (values?.expense_transportation) {
            formData.append('transportation', JSON.stringify(values?.expense_transportation))
          }
          break;
        case 'Office supply / stationery':
          if (values?.expense_office_supplies) {
            formData.append('office_supply', JSON.stringify(values?.expense_office_supplies))
          }
          break;
        case 'Printing':
          if (values?.expense_printing) {
            formData.append('printing', JSON.stringify(values?.expense_printing))
          }
          break;
        case 'Other reimbursement':
          if (values?.expense_other_reimbursement) {
            formData.append('other_reimbursement', JSON.stringify(values?.expense_other_reimbursement))
          }
          break;
        default:
          break;
      }
    }
    
    return formData
  }

  const handlePost = (payload) => {
    dispatch({
      type: 'SET_IS_LOADING',
      isLoading: true
    })
    Axios({
      method: "post",
      url: `/api/tracker/v1/expense/create`,
      data: payload
    })
      .then(({ data }) => {
        if (data?.success) {
          showSuccess('New expense has been successfully created')
          navigate('/expense/list')
        }
      })
      .catch((err) => {
        console.error(err);
        const errMsg = err?.response?.data?.message
        showError(errMsg)
      })
      .finally(() => {
        dispatch({
          type: 'SET_IS_LOADING',
          isLoading: false
        })
      })
  }

  const handleSave = (values) => {
    const inputPayload = handleInputValue(values)
    handlePost(inputPayload)
  }

  const handleSelectProject = (data) => {
    formik.setValues((values) => ({
      ...values,
      project_id: data?.value
    }), false)

    setProjectValue(data)
  }

  const handleSelectExpenseCategory = (data) => {
    formik.setValues((values) => ({
      ...values,
      category_id: data?.value,
      category_value: data
    }), false)

    setExpenseCategoryValue(data)

    if (data) {
      if (data?.label == 'Accomodation') {
        fetchAllCountries()
      }
    }
  }

  const handleSelectExpenseInterval = (data) => {
    formik.setValues((values) => ({
      ...values,
      expense_repeat_interval: data?.value,
      expense_repeat_start_date: data?.value == 'no repeat' ? '' : values.expense_repeat_start_date,
      expense_repeat_end_date: data?.value == 'no repeat' ? '' : values.expense_repeat_end_date,
      expense_date: data?.value == 'no repeat' ? values.expense_date : ''
    }), false)

    setIntervalValue(data)
  }

  const handleFileInput = (values) => {
    const files = values

    if (files) {
      formik.setValues((prevValues) => ({
        ...prevValues,
        file: files
      }), false)
    }
  }

  const handleRemoveFile = () => {
    formik.setValues((prevValues) => ({
      ...prevValues,
      file: []
    }), false)

    setShowDeleteItem({ show: false, item: {} })
  }

  const handleSwitchRepeatForever = (e) => {
    let interval = formik.values.expense_repeat_interval
    
    if (
      formik.values.expense_repeat_interval == 'no repeat' &&
      e.target.checked == true
    ) {
      interval = ''
      setIntervalValue(null)
    }

    formik.setValues((prevValues) => ({
      ...prevValues,
      expense_repeat_forever: e.target.checked,
      expense_repeat_interval: interval
    }), false)
  }

  const handleSubmit = () => {
    formik.handleSubmit()

    setShowModalConfirm({ show: false })
  }

  const handleChangeFieldCategory = (e, category) => {
    const name = e?.target?.name
    const value = e?.target?.value
    const checked = e?.target?.checked
    
    formik.setValues((values) => ({
      ...values,
      [category]: {
        ...values[category],
        [name]: name == 'is_booking_more_than_one_day' ? checked : value
      }
    }), false)
  }

  const handleOnDateChangeCategory = (date, name, category) => {
    formik.setValues((values) => ({
      ...values,
      [category]: {
        ...values[category],
        [name]: moment(date).format('YYYY-MM-DD HH:mm:ss')
      }
    }), false)
  }

  const handleSelectOptionExpenseCategory = (data, name, category, isMultiple) => {    
    formik.setValues((values) => ({
      ...values,
      [category]: {
        ...values[category],
        [name]: isMultiple ? data?.length > 0 ? data?.map((val) => {
          return {
            label: val?.label,
            value: val?.value
          }
        }) : [] : data ? data?.value : null
      }
    }), false)

    if (category == 'expense_flight') {
      if (name == 'booking_through') {
        setExpenseFlightValue(data)
      }
      if (name == 'airplane_name') {
        setExpenseAirplaneValue(data)
      }
    }

    if (category == 'expense_train') {
      if (name == 'booking_through') {
        setExpenseTrainValue(data)
      }
    }

    if (category == 'expense_transportation') {
      setExpenseTransportationValue(data)
    }

    if (category == 'expense_fuel') {
      setExpenseFuelValue(data)
    }

    if (category == 'expense_accomodation') {
      if (name == 'booking_through') {
        setExpenseAccomodationValue(data)
      }
      if (name == 'accomodation_province_id') {
        fetchAllCities(data?.value)
        setExpenseAccomodationProvinceValue(data)
      }
      if (name == 'accomodation_city_id') {
        setExpenseAccomodationCitiesValue(data)
      }
    }
  }

  const handleChangePassenger = (e, index, category) => {
    const name = e?.target?.name;
    const value = e?.target?.value;

    let path = `${category}.passengers[${index}].[${name}]`;

    if (category == 'expense_accomodation') {
      path = `${category}.guests[${index}].[${name}]`;
    }
    
    formik.setFieldValue(path, value, false);
  };

  const handleAddPassenger = (category) => {
    formik.setValues((values) => ({
      ...values,
      [category]: {
        ...values[category],
        [category == 'expense_accomodation' ? 'guests' : 'passengers']: [
          ...values[category]?.[category == 'expense_accomodation' ? 'guests' : 'passengers'],
          ...[
            {
              first_name: '',
              last_name: ''
            }
          ]
        ]
      }
    }), false)
  }

  const handleRemovePassenger = (index, category) => {
    formik.setValues((values) => ({
      ...values,
      [category]: {
        ...values[category],
        [category == 'expense_accomodation' ? 'guests' : 'passengers']: values[category]?.[category == 'expense_accomodation' ? 'guests' : 'passengers']?.filter((_, itemIndex) => itemIndex !== index)
      }
    }), false)
  }

  return (
    <>
      {openFilePreview && (
        <AppModalFilePreview
          setOpenFilePreview={setOpenFilePreview}
          file={openFilePreview}
        />
      )}
      {
        showDeleteItem.show && (
          <AppModalConfirmation 
            confirmationType='delete-item'
            itemName={showDeleteItem?.item?.name}
            itemDetails={showDeleteItem.item}
            itemType={showDeleteItem.type}
            itemIndex={showDeleteItem?.index}
            showModal={showDeleteItem}
            setShowModal={setShowDeleteItem}
            handleConfirmation={handleRemoveFile}
          />
        )
      }
      {
        showModalConfirm.show && (
          <AppModalConfirmation 
            confirmationType="confirmation"
            showModal={showModalConfirm}
            setShowModal={setShowModalConfirm}
            headerTitle="Confirmation"
            descriptionText="Are you sure want create a new expenses?"
            handleSubmit={handleSubmit}
            disabled={formik.isSubmitting}
            isLoading={isLoading}
          />
        )
      }
      <AppCard 
        className='mb-4'
        headerTitle={<h4>Add Expense</h4>}
        bodyContent={
          <AppForm 
            values={formik.values}
            handleChange={formik.handleChange}
            errors={formik.errors}
            onDateChange={handleOnDateChange}
            handleFileInput={handleFileInput}
            projectOptions={projectOptions}
            handleSelectProject={handleSelectProject}
            expenseIntervalOptions={singleExpenseIntervalOptionSelect(formik.values.expense_repeat_forever)}
            handleSelectInterval={handleSelectExpenseInterval}
            handleConfirmDelete={setShowDeleteItem}
            isShowDropdown={true}
            projectValue={projectValue}
            intervalValue={intervalValue}
            handleSwitchRepeatForever={handleSwitchRepeatForever}
            handleViewFile={setOpenFilePreview}
            expenseCategoryOptions={expenseCategoryOptions}
            expenseCategoryValue={expenseCategoryValue}
            expenseTagsOptions={expenseTagsOptions}
            handleSelectExpenseCategory={handleSelectExpenseCategory}
            handleChangeFieldCategory={handleChangeFieldCategory}
            handleOnDateChangeCategory={handleOnDateChangeCategory}
            expenseTransportationOptions={singleExpenseCategoryTransportOptionSelect()}
            expenseFuelOptions={singleExpenseCategoryFuelOptionSelect()}
            handleSelectOptionExpenseCategory={handleSelectOptionExpenseCategory}
            expenseTransportationValue={expenseTransportationValue}
            expenseFuelValue={expenseFuelValue}
            tripTypeOptions={singleExpenseCategoryTripTypeOptionSelect()}
            bookingThroughFlighOptions={singleExpenseCategoryFlightOptionSelect()}
            bookingThroughTrainOptions={singleExpenseCategoryTrainOptionSelect()}
            expenseFlightValue={expenseFlightValue}
            expenseTrainValue={expenseTrainValue}
            handleChangePassenger={handleChangePassenger}
            handleAddPassenger={handleAddPassenger}
            handleRemovePassenger={handleRemovePassenger}
            expenseAirplaneOptions={singleExpenseCategoryAirplaneOptionSelect()}
            expenseAirplaneValue={expenseAirplaneValue}
            bookingThroughAccomodationOptions={singleExpenseCategoryAccomodationOptionSelect()}
            expenseAccomodationValue={expenseAccomodationValue}
            provinceOptions={provinceOptions}
            cityOptions={cityOptions}
            expenseAccomodationProvinceValue={expenseAccomodationProvinceValue}
            expenseAccomodationCitiesValue={expenseAccomodationCitiesValue}
          />
        }
        footerContent={
          <ActionButtonForm
            isLoading={isLoading}
            disabled={formik.isSubmitting}
            onCancel={() => navigate('/expense/list')}
            onSave={() => setShowModalConfirm({ show: true })}
          />
        }
      />
    </>
  )
}

export default AddExpense